import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import Http from "../../Http";
import { Insurance_API } from "../../Helper";
import {
  FaUserShield,
  FaUsers,
  FaUserPlus,
  FaHeartbeat,
  FaBriefcaseMedical,
} from "react-icons/fa";
import {
  FaHospitalUser,
  FaMoneyBillWave,
  FaShieldAlt,
  FaStethoscope,
  FaWallet,
  FaUserMd,
  FaPiggyBank,
} from "react-icons/fa";
import { ArrowRight } from "lucide-react";

function Icon({ id, open }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={2}
      stroke="currentColor"
      className={`${
        id === open ? "rotate-180" : ""
      } h-5 w-5 transition-transform`}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M19.5 8.25l-7.5 7.5-7.5-7.5"
      />
    </svg>
  );
}

const HealthInsuranceNew = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [insuranceData, setInsuranceData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const faqs = [
    {
      question: "What is health insurance?",
      answer:
        "Health insurance is a financial arrangement that helps cover medical expenses, such as doctor visits, hospital stays, prescription medications, and other healthcare costs. It provides a safety net, ensuring that you can afford necessary medical care when you need it.",
    },
    {
      question:
        "What factors should I consider when choosing a health insurance policy?",
      answer:
        "When choosing a health insurance policy, consider factors like the coverage details, hospitals covered, monthly premiums and the network of healthcare providers. You should also consider the coverage for specific medical needs, such as prescriptions, specialist visits, or maternity care.",
    },
    {
      question: "Why is health insurance important?",
      answer:
        " Health insurance is important because it provides financial protection against high medical costs. It helps you access necessary healthcare services without worrying about the expense. Without health insurance, a serious illness or injury could lead to significant financial burdens, potentially causing debt or bankruptcy.",
    },
    {
      question: "What is the waiting period in health insurance?",
      answer:
        "  A waiting period is the time you must wait before certain benefits of your health insurance policy become active. Common waiting periods include 30 days for general claims and 2-4 years for pre-existing diseases or specific treatments.",
    },
    {
      question: "Does health insurance cover pre-existing conditions?",
      answer:
        " Most health insurance policies cover pre-existing conditions only after a waiting period, typically ranging from 2 to 4 years. It's essential to disclose these conditions while purchasing the policy to avoid claim rejections later.",
    },
    {
      question: "What is a cashless health insurance facility?",
      answer:
        "  Cashless insurance allows you to get treatment at network hospitals without paying upfront. The insurer directly settles the hospital bill, provided the treatment is covered and the hospital is within the insurer’s tie-up list.",
    },
    {
      question: "What is the difference between individual and family floater health insurance?",
      answer:
        "  Individual health insurance covers only one person, while a family floater plan covers the entire family under a single sum insured. The floater plan is more cost-effective but the entire sum insured is shared among all members.",
    },
    {
      question: "Can I have multiple health insurance policies?",
      answer:
        "  Yes, you can hold multiple health insurance policies. In case of a large medical bill, you can claim from the first insurer up to the sum insured, and then approach the second insurer for the remaining amount using a process called contribution.",
    },
    {
      question: "What is a top-up or super top-up health plan?",
      answer:
        " Top-up and super top-up plans offer additional coverage beyond a deductible limit. They are cost-effective ways to increase your health coverage without buying a completely new high-premium policy.",
    },
    {
      question: "Are health check-ups covered under health insurance?",
      answer:
        " Yes, many health insurance plans include preventive health check-ups either annually or after a few claim-free years. Some plans offer cash benefits or reimbursements for such check-ups up to a certain limit.",
    },
    {
      question: "What is No Claim Bonus (NCB) in health insurance?",
      answer:
        "  No Claim Bonus is a reward for not making any claims in a policy year. It usually comes as an increase in sum insured at no extra cost or a discount on the premium during renewal, depending on the insurer’s terms.",
    },
    {
      question: "How is the health insurance premium calculated?",
      answer:
        " Premium is calculated based on factors like age, medical history, lifestyle, policy type, sum insured, and city of residence. Higher coverage or older age results in higher premiums due to increased risk for the insurer.",
    },
    {
      question: "Does health insurance cover maternity expenses?",
      answer:
        " Some health insurance plans offer maternity coverage, including delivery costs and newborn expenses. These usually come with a waiting period of 9 months to 2 years and may have sub-limits on claimable expenses.",
    },
    {
      question: "What is a pre and post-hospitalization cover?",
      answer:
        "  Pre-hospitalization covers medical expenses incurred before hospital admission (usually 30 days), while post-hospitalization covers follow-up treatment costs after discharge (typically up to 60-90 days), if related to the same ailment.",
    },
    {
      question: "What is domiciliary hospitalization?",
      answer:
        "  Domiciliary hospitalization refers to treatment taken at home when hospital admission isn’t possible due to non-availability of hospital beds or the patient’s condition. Some policies cover this under specific conditions and limits.",
    },
    {
      question: "Can I port my health insurance policy to another insurer?",
      answer:
        "  Yes, health insurance portability allows you to switch to another insurer without losing the benefits like waiting period credit. The request must be made at least 45 days before the policy renewal date.",
    },
    {
      question: "Is AYUSH treatment covered in health insurance?",
      answer:
        "   Many insurers now offer coverage for AYUSH (Ayurveda, Yoga, Unani, Siddha, and Homeopathy) treatments. These are usually covered only if taken at government-approved or NABH-accredited centers.",
    },
    {
      question: "How do I file a health insurance claim?",
      answer:
        "   You can file a claim either cashless (at a network hospital) or via reimbursement. For cashless claims, inform the insurer and get approval before treatment. For reimbursement, submit hospital bills, reports, and forms after discharge.",
    },
  ];
  const HealthinsuranceTypes = [
    {
      title: "Individual Health Insurance",
      description:
        "Designed for a single policyholder, this plan provides dedicated coverage, ensuring that the sum insured is available exclusively for the insured person’s medical needs. It covers hospitalization expenses, treatments, and other medical costs, offering financial assistance in times of emergency.",
      icon: <FaUserShield className="text-4xl text-blue-600" />,
    },
    {
      title: "Family Health Insurance",
      description:
        "A family health insurance plan provides coverage to all family members under a single policy. Instead of purchasing separate plans for each individual, the sum insured is shared among the insured members. This plan is a cost-effective way to secure the health of the entire family while ensuring adequate financial protection.",
      icon: <FaUsers className="text-4xl text-green-600" />,
    },
    {
      title: "Senior Citizen Health Insurance",
      description:
        "Specially designed for individuals aged 60 and above, senior citizen health insurance covers age-related medical expenses and illnesses. These plans often include additional benefits like regular health check-ups, coverage for pre-existing conditions, and critical care support, ensuring that elderly individuals receive proper medical attention without financial strain.",
      icon: <FaUserPlus className="text-4xl text-orange-600" />,
    },
    {
      title: "Critical Illness Insurance",
      description:
        "A critical illness plan provides a lump sum payout upon diagnosis of life-threatening diseases such as cancer, heart attack, stroke, kidney failure, or organ transplant. This financial assistance helps the policyholder manage high medical costs, long-term treatments, and post-recovery expenses, reducing the economic burden during a health crisis.",
      icon: <FaHeartbeat className="text-4xl text-red-600" />,
    },
    {
      title: "Group Health Insurance",
      description:
        "Offered by employers or organizations to their employees, group health insurance covers a group of individuals under a single plan. It typically includes hospitalization benefits, daycare treatments, and preventive healthcare services. The cost of such policies is generally lower due to group discounts, making it an affordable healthcare solution for companies and their workforce.",
      icon: <FaBriefcaseMedical className="text-4xl text-purple-600" />,
    },
  ];
  const benefitshealth = [
    {
      title: "Protection Against Rising Medical Costs",
      description:
        "Medical treatments are becoming increasingly expensive due to advancements in healthcare technology. Health insurance ensures you can afford quality treatment without exhausting your savings.",
      icon: <FaMoneyBillWave className="text-4xl text-blue-600" />,
    },
    {
      title: "Access to Quality Medical Facilities",
      description:
        "Without health insurance, the cost of top-tier healthcare services can be overwhelming. A policy gives you access to superior medical care, including pre- and post-hospitalization benefits, ensuring faster recovery.",
      icon: <FaHospitalUser className="text-4xl text-green-600" />,
    },
    {
      title: "Financial Security During Medical Emergencies",
      description:
        "Life is unpredictable, and medical emergencies can strike at any time. Health insurance covers hospitalization, surgeries, and critical treatments, allowing you to focus on recovery rather than financial worries.",
      icon: <FaShieldAlt className="text-4xl text-orange-600" />,
    },
    {
      title: "Specialized Coverage for Critical Illnesses",
      description:
        "Chronic and life-threatening diseases like cancer, heart conditions, and kidney ailments are on the rise. Critical illness insurance provides a lump-sum payout to manage expensive treatments and long-term care.",
      icon: <FaHeartbeat className="text-4xl text-red-600" />,
    },
    {
      title: "Cashless Claim Benefits",
      description:
        "Many insurers have a network of hospitals where you can receive treatment without making upfront payments, reducing the burden of arranging funds during emergencies.",
      icon: <FaWallet className="text-4xl text-purple-600" />,
    },
    {
      title: "Tax Benefits Under Section 80D",
      description:
        "Under the old tax regime, policyholders can claim deductions on their health insurance premiums, reducing taxable income and maximizing savings.",
      icon: <FaStethoscope className="text-4xl text-indigo-600" />,
    },
    {
      title: "Safeguards Your Savings",
      description:
        "Medical expenses can drain years of savings. A health insurance plan ensures that you and your family receive the necessary treatment without compromising financial stability.",
      icon: <FaPiggyBank className="text-4xl text-indigo-600" />,
    },
    {
      title: "Better Health Outcomes Through Preventive Care",
      description:
        "Regular health check-ups, screenings, and early disease detection become more accessible with insurance, promoting long-term well-being.",
      icon: <FaUserMd className="text-4xl text-indigo-600" />,
    },
  ];
  const points1 = [
    {
      title: " In-Patient Hospitalization ",
      description: `Covers medical expenses incurred during hospitalization due to illness or injury, provided the hospital stay exceeds 24 hours.`,
    },
    {
      title: "Pre-Existing Disease Coverage",
      description: `After completing the waiting period, expenses for the treatment of pre-existing illnesses or conditions are covered.`,
    },
    {
      title: "Pre & Post-Hospitalization Expenses",
      description: `Covers diagnostic tests, medical check-ups, medications, and follow-up treatments related to hospitalization.`,
    },
    {
      title: "Ambulance Charges",
      description: `Provides coverage for emergency ambulance services to transport the insured to the nearest hospital.`,
    },
    {
      title: "Maternity and Newborn Cover",
      description: `Covers medical expenses related to pregnancy, childbirth, and newborn care, as per policy terms.`,
    },
    {
      title: "Preventive Health Check-ups ",
      description: `Enables policyholders to undergo routine health check-ups to detect potential health risks early.`,
    },
    {
      title: "Day-Care Procedures",
      description: `Covers medical procedures that do not require a 24-hour hospital stay, such as cataract surgery, dialysis, and chemotherapy.`,
    },
    {
      title: "Home Treatment Coverage",
      description: `Covers medical expenses for treatment at home if hospitalization is not feasible, as advised by a doctor.`,
    },
    {
      title: "AYUSH Treatment",
      description: `Provides coverage for alternative treatments like Ayurveda, Unani, Siddha, Homeopathy, and Yoga-based medical care.`,
    },
    {
      title: "Mental Healthcare Coverage",
      description: `As per the Mental Healthcare Act, 2017, all health plans cover hospitalization expenses for the treatment of mental illnesses such as depression, schizophrenia, and bipolar disorder.`,
    },
  ];
  const points2 = [
    {
      title: "Initial Waiting Period Claims ",
      description: `Claims made within the first 30 days of purchasing the policy (except for accidental emergencies) are not covered.`,
    },
    {
      title: " Pre-existing Diseases Waiting Period",
      description: `Pre-existing conditions are covered only after a waiting period of 2 to 4 years.`,
    },
    {
      title: ". Critical Illness Waiting Period",
      description: `Coverage for critical illnesses is subject to a waiting period of 90 days.`,
    },
    {
      title: "War and Terrorism-Related Injuries",
      description: `Injuries due to war, terrorism, or nuclear activity are excluded.`,
    },
    {
      title: " Self-Inflicted Injuries and Suicide Attempts",
      description: `Intentional self-harm, including suicide attempts, is not covered.`,
    },
    {
      title: " Terminal Illnesses and Similar Conditions",
      description: `Certain terminal illnesses may not be covered under standard policies.`,
    },
    {
      title: "Cosmetic/Plastic Surgery and Hormone Replacement Therapy",
      description: `Unless medically necessary, cosmetic and hormonal treatments are excluded.`,
    },
    {
      title: "Non-Accidental Dental Treatments",
      description: `Routine dental procedures not resulting from an accident are not covered.`,
    },
    {
      title: "Bed Rest and Rehabilitation",
      description: `Costs related to rest cures and rehabilitation programs are excluded.`,
    },

    {
      title: "Diagnostic Tests",
      description: `Medical tests conducted without hospitalization are generally not covered.`,
    },
    {
      title: "Adventure Sports Injuries",
      description: `Claims arising from adventure sports activities like skydiving, bungee jumping, or scuba diving are not covered.`,
    },
  ];
  const points3 = [
    {
      title: "Coverage & Benefits",
      points: [
        "Check if the policy covers hospitalization, pre-existing conditions, maternity expenses, and day-care procedures.",
        "Look for add-on benefits like mental healthcare, domiciliary treatment, and AYUSH coverage.",
      ],
    },
    {
      title: "Waiting Periods",
      points: [
        "Understand the waiting period for pre-existing diseases, maternity coverage, and critical illnesses.",
        "Opt for a policy with a shorter waiting period if possible.",
      ],
    },
    {
      title: "Sum Insured",
      points: [
        "Ensure the sum insured is adequate to cover rising healthcare costs.",
        "Consider your city’s medical expenses while choosing the coverage amount.",
      ],
    },
    {
      title: "Cashless Network Hospitals",
      points: [
        "Check the list of network hospitals where you can avail cashless treatment.",
        "Ensure reputed hospitals near your location are included in the insurer’s network.",
      ],
    },
    {
      title: "Claim Settlement Ratio",
      points: [
        "A high claim settlement ratio indicates a reliable insurer.",
        "Read customer reviews to understand the insurer’s claim settlement process.",
      ],
    },
    {
      title: "Premium & Affordability",
      points: [
        "Compare premiums for different plans and choose one that fits your budget.",
        "Avoid choosing the cheapest plan if it compromises on coverage.",
      ],
    },
    {
      title: "Co-Payment & Deductibles",
      points: [
        "Some policies require you to pay a percentage of the claim amount.",
        "Opt for a policy with a lower co-payment or deductible for better financial coverage.",
      ],
    },
    {
      title: "No Claim Bonus (NCB)",
      points: [
        "If you don’t make any claims in a year, your sum insured increases without extra cost.",
        "Look for policies offering higher NCB benefits.",
      ],
    },
    {
      title: "Exclusions & Limitations",
      points: [
        "Carefully read what is NOT covered in the policy, such as cosmetic surgeries or adventure sports injuries.",
        "Be aware of sub-limits on room rent, ICU charges, or specific treatments.",
      ],
    },
    {
      title: "Additional Riders",
      points: [
        "Check for optional add-ons like critical illness cover, accidental disability cover, and OPD expenses.",
        "Customize your policy with riders that suit your needs.",
      ],
    },
    {
      title: "Lifetime Renewability",
      points: [
        "Choose a policy that offers lifetime renewability to stay covered even in old age.",
      ],
    },
    {
      title: "Customer Support & Claim Process",
      points: [
        "Ensure the insurer has a hassle-free and quick claim settlement process.",
        "24/7 customer support and a good grievance redressal system are crucial.",
      ],
    },
  ];
  const points4 = [
    {
      title: "Age of the Insured",
      points: [
        "Younger individuals get lower premiums as they are at lower risk of illnesses.",
        " Premiums increase with age, and senior citizens may have limited coverage options.",
      ],
    },
    {
      title: "Pre-Existing Medical Conditions",
      points: [
        " If you have pre-existing diseases (e.g., diabetes, hypertension), insurers may impose waiting periods or higher premiums.",
        " Some insurers may even reject applications based on severe pre-existing conditions.",
      ],
    },
    {
      title: "Sum Insured & Coverage Amount",
      points: [
        " Higher sum insured means better financial protection but also results in higher premiums.",
        "The ideal sum insured should align with your healthcare needs and inflation.",
      ],
    },
    {
      title: "Type of Policy & Coverage",
      points: [
        " Individual plans, family floater plans, and group health insurance have different premium structures.",
        "Comprehensive plans with maternity benefits, OPD cover, and critical illness riders cost more.",
      ],
    },
    {
      title: " Lifestyle Habits",
      points: [
        "Healthcare costs vary across cities; metro areas have higher premiums due to expensive medical facilities.",
        "Some insurers offer region-based pricing depending on where you reside.",
      ],
    },
    {
      title: "Waiting Periods",
      points: [
        "Policies have waiting periods (2-4 years) for pre-existing conditions, maternity benefits, and specific treatments.",
        " A shorter waiting period means better coverage but can increase premiums.",
      ],
    },
    {
      title: "Network Hospitals & Cashless Facilities",
      points: [
        " A large network of hospitals ensures easy access to cashless treatment.",
        " Limited hospital coverage may result in out-of-pocket expenses.",
      ],
    },
    {
      title: "No Claim Bonus (NCB)",
      points: [
        "If no claims are made in a year, insurers offer NCB, increasing the sum insured without extra cost.",
        "Higher NCB benefits can help reduce long-term expenses.",
      ],
    },
    {
      title: "Co-Payment & Deductibles",
      points: [
        "Some policies require policyholders to pay a portion of the claim (co-payment).",
        " Choosing a policy with lower co-payment ensures better financial protection.",
      ],
    },
    {
      title: " Inflation & Rising Medical Costs",
      points: [
        "Healthcare inflation increases treatment costs, affecting policy premiums and sum insured adequacy.",
        "It’s advisable to choose a plan with an increasing sum insured benefit.",
      ],
    },
    {
      title: "Claim Settlement Ratio & Insurer’s Reputation",
      points: [
        "A high claim settlement ratio indicates a reliable insurer.",
        " Research customer feedback and reviews to check the insurer’s claim processing efficiency.",
      ],
    },
    {
      title: "Government Regulations & Tax Benefits",
      points: [
        "Health insurance policies in India follow IRDAI guidelines, ensuring consumer protection.",
        "Premiums paid qualify for tax benefits under Section 80D of the Income Tax Act.",
      ],
    },
  ];
  const data = [
    {
      plan: "Individual Health Insurance",
      tier1: "₹10 lakh & above",
      tier2: "₹5-10 lakh",
      tier3: "₹5 lakh",
    },
    {
      plan: "Family Floater Plan",
      tier1: "₹30 lakh & above",
      tier2: "₹20 lakh & above",
      tier3: "₹10 lakh & above",
    },
    {
      plan: "Senior Citizen Plan",
      tier1: "₹20 lakh & above",
      tier2: "₹15 lakh & above",
      tier3: "₹10 lakh & above",
    },
  ];
  const toggleAccordion = (index) => {
    if (activeIndex === index) {
      // If the clicked question is already expanded, collapse it
      setActiveIndex(null);
    } else {
      // If the clicked question is not expanded, expand it
      setActiveIndex(index);
    }
  };

  useEffect(() => {
    fetchInsuranceData();
  }, []);
  const fetchInsuranceData = async () => {
    try {
      const response = await Http.get(Insurance_API);

      // The response structure might have nested data
      // Check if response has an insurance property (like in the original fetch response)
      const data = response.insurance || response;

      // Filter the insurance data to show only Car category
      const carInsuranceData = Array.isArray(data)
        ? data.filter((insurance) => insurance.category_name === "Health")
        : [];

      setInsuranceData(carInsuranceData);
      setLoading(false);
    } catch (error) {
      setError("Failed to fetch insurance data");
      setLoading(false);
      console.error("Error fetching data:", error);
    }
  };
  // const fetchInsuranceData = async () => {
  //     try {
  //         const response = await fetch('https://test.thefinmaster.com/console/react-app-directory/insurance-api.php');
  //         if (!response.ok) {
  //             throw new Error('Network response was not ok');
  //         }
  //         const data = await response.json();
  //         setInsuranceData(data.insurance || []);
  //         setLoading(false);
  //     } catch (error) {
  //         setError('Failed to fetch insurance data');
  //         setLoading(false);
  //         console.error('Error fetching data:', error);
  //     }
  // };
  return (
    <>
      <Helmet>
        <title>
          Health Insurance: Buy Best Health Insurance Plan Online in India May,
          2024
        </title>
        <meta
          name="description"
          content="Buy health insurance plan with Thefinmaster.com as we explore the benefits and importance, key features, inclusions as well as exclusions of a medical insurance plans."
        />
        <meta
          name="keywords"
          content="health insurance quotes, health care insurance, government health insurance, "
        />
        <link
          rel="canonical"
          href="https://thefinmaster.com/health_insurance"
        />
        <meta
          name="robots"
          content="max-snippet:-1, max-image-preview:standard"
        />
        <meta name="robots" content="max-video-preview:-1" />
      </Helmet>
      <nav className="text-gray-500 text-sm my-4 ml-10" aria-label="Breadcrumb">
        <ol className="list-none p-0 inline-flex">
          <li className="flex items-center">
            <Link to="/" className="text-blue-600 hover:text-blue-800 ">
              Home
            </Link>
            <svg
              className="w-4 h-4 mx-2 text-gray-500"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </li>

          <li className="flex items-center">
            <span className="text-gray-500 ">Health Insurance</span>
          </li>
          {/* <li className="flex items-center">
                  <Link to="/credit-card" className="text-blue-600 hover:text-blue-800">Credit Card</Link>
                  <svg className="w-4 h-4 mx-2 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z" clipRule="evenodd" />
                  </svg>
                </li> */}
          {/* <li className="flex items-center">
                  <span className="text-gray-500">Car Loan EMI Calculator</span>
                </li> */}
        </ol>
      </nav>
      {/* Your existing content */}
      <div className="w-full flex flex-col items-center">
        <div className="w-full flex justify-center">
          <div className="w-full sm:w-[95%] flex flex-col sm:flex-row justify-center items-center">
            <div className="w-full sm:w-[50%]">
              <div className="w-[90%]">
                <h1 className="text-4xl md:text-5xl font-bold mt-6 md:mt-0 md:pl-0 pl-6">
                  <span className="text-blue-600 ">Protect Your Health,</span>{" "}
                  Secure Your Future!
                </h1>
                {/* <a href="#insurance_plans">
                  <button className="py-4 px-8  border rounded-full bg-blue-600 text-white shadow-lg hover:shadow-xl text-lg mt-10 font-medium">
                    See Plans
                  </button>
                </a> */}
              </div>
            </div>
            <div className="w-full sm:w-[50%]">
              <img
                className="w-full max-w-full"
                src="../assets/health.webp"
                alt="car-loan"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="shadow-lg rounded-lg p-4 md:p-8 lg:p-12 mb-6 px-6 mx-4 md:mx-8 lg:mx-10 text-gray-600 flex justify-center items-center">
        <p className="text-lg md:text-base lg:text-lg text-center">
          TheFinMaster.com is here to guide you towards a secure and healthy
          future. We've partnered with leading health insurance providers to
          offer you a wide range of plans to choose from. Compare benefits,
          understand coverage details, and get free quotes – all in one
          convenient location. Find the perfect health insurance plan to protect
          your well-being, leaving the financial worries to us.
        </p>
      </div>

      <div className="container mx-auto">
      <div className="flex flex-col lg:flex-row">
        {/* Main content area with insurance listings */}
        <div className="w-full lg:w-3/4">
          <div id="insurance_plans" className="lg:pr-6 md:px-4 px-2">
            {loading ? (
              <div className="text-center py-10">Loading insurance options...</div>
            ) : error ? (
              <div className="text-center py-10 text-red-600">{error}</div>
            ) : insuranceData.length === 0 ? (
              <div className="text-center py-10">
                No car insurance options available at the moment.
              </div>
            ) : (
          insuranceData.map((insurance) => (
            <div
              key={insurance.id}
              className="md:w-full max-w-[900px] py-2 border shadow-lg overflow-hidden hover:shadow-xl rounded-lg flex flex-wrap md:flex-nowrap gap-5 justify-between mx-auto mt-4"
            >
              <div className="flex flex-wrap md:flex-nowrap w-full justify-center items-center md:justify-normal">
                <Link to={`/health_insurance/${insurance.page_url}`}>
                  <div className="w-[95%] md:w-[220px] overflow-hidden py-2 md:ml-2">
                    <img
                      className="w-full h-full object-contain"
                      alt={insurance.page_title || "Insurance Provider"}
                      src={insurance.image}
                    />
                  </div>
                </Link>
              </div>

              <div className="w-full md:pl-0 pl-4 mt-2 md:mt-6">
                <p className="text-gray-500 font-medium text-sm md:text-base">Premium Starting </p>
                <h4 className="text-xl md:text-2xl py-0.5 font-bold">
                  @ ₹{insurance.premium_start}
                </h4>
              </div>

              <div className="w-full md:pl-0 pl-4 mt-2 md:mt-6">
                <p className="text-gray-500 font-medium text-sm md:text-base">Claim Settlement </p>
                <h4 className="text-xl md:text-2xl py-0.5 font-bold">
                  {insurance.claim_settlement}%
                </h4>
              </div>

              <div className="w-full md:pl-0 pl-4 mt-2 md:mt-6">
                <p className="text-gray-500 font-medium text-sm md:text-base">Hospital Networks</p>
                <h4 className="text-xl md:text-2xl py-0.5 font-bold">
                  {insurance.network_garages}+
                </h4>
              </div>

              <div className="flex flex-wrap justify-center md:justify-between items-center my-5 w-full gap-2 px-4">
                <a
                  href={insurance.bank_link}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <button className="bg-green-500 text-white font-semibold py-2 px-4 rounded hover:bg-green-600 transition duration-300">
                    Get Quote!
                  </button>
                </a>

                <Link to={`/health_insurance/${insurance.page_url}`}>
                  <button className="text-white font-semibold py-2 px-4 rounded hover:bg-blue-600 transition duration-300 bg-blue-500">
                  See Details
                  </button>
                </Link>
              </div>
            </div>
          ))
        )}
      </div>
      </div>

<div className="w-full lg:w-1/4 px-2 md:px-4 mt-6 lg:mt-0">
<div className="sticky top-20">
  {/* Ad 1 - Visible on all screen sizes */}
  <a href="https://play.google.com/store/apps/details?id=com.popclub.android&pcampaignid=web_share" target="_blank" rel="noopener noreferrer" className="block">
    <img 
      src="/assets/ppp.png" 
      alt="Insurance Special Offer" 
      className="w-full rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300"
    />
  </a>
  
  {/* Ad 2 - Hidden on small mobile, visible on larger screens */}
  {/* <a href="https://example.com/premium-discount" target="_blank" rel="noopener noreferrer" className="hidden sm:block mt-6">
    <img 
      src="/assets/niyoo.png" 
      alt="Premium Discount Offer" 
      className="w-full rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300"
    />
  </a> */}
  
  {/* Compact ad for mobile only */}
  {/* <a href="https://example.com/mobile-offer" target="_blank" rel="noopener noreferrer" className="block sm:hidden mt-4">
    <img 
      src="/assets/niyoo.png" 
      alt="Mobile Special Offer" 
      className="w-full rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300"
    />
  </a> */}
</div>
</div>
</div>
</div>
<section className="bg-blue-50 py-16 px-6 sm:px-12 lg:px-20 rounded-3xl shadow-lg max-w-7xl mx-auto mt-10">
  <div className="flex flex-col md:flex-row justify-between items-center gap-10">
    {/* Left - Text and CTA */}
    <div className="max-w-xl space-y-6 text-center md:text-left">
      <h2 className="text-2xl sm:text-3xl font-bold text-blue-900 leading-tight">
        Calculate Your Health Insurance <br />
        in <span className="text-blue-600">Seconds</span>
      </h2>
      <p className="text-gray-700 text-lg">
        Use our fast and free calculator to estimate your premium — no paperwork, no waiting.
      </p>
      <Link
        to="/financial-tools-calculators/health-insurance-calculator"
        className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 text-lg rounded-full shadow-md transition"
      >
        Start Calculating <ArrowRight className="w-5 h-5" />
      </Link>
    </div>

    {/* Right - Feature List with Emojis */}
    <div className="space-y-4 text-left">
      <div className="flex items-center gap-3">
        <span className="text-xl">🛡️</span>
        <span className="text-gray-800 font-medium">Reliable & Accurate Estimates</span>
      </div>
      <div className="flex items-center gap-3">
        <span className="text-xl">⏱️</span>
        <span className="text-gray-800 font-medium">Takes Less Than a Minute</span>
      </div>
    </div>
  </div>
</section>
      <div className="flex justify-center items-center  p-4 mt-6">
        <div className="bg-white shadow-lg rounded-2xl p-6 max-w-7xl text-center ">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-4">
            What is Health Insurance?
          </h2>
          <p className="text-gray-600 leading-relaxed">
            Health insurance is a financial safeguard that covers medical and
            surgical expenses arising from illnesses, injuries, and preventive
            healthcare. It ensures that policyholders receive the necessary
            medical treatment without the burden of high healthcare costs.
            Depending on the policy, health insurance can either reimburse the
            insured for expenses incurred or directly settle bills with
            hospitals and healthcare providers. In India, these policies can be
            purchased from insurance companies or provided by employers,
            covering hospitalization costs, doctor consultations, diagnostic
            tests, and medications. Claims are settled either through a cashless
            facility, where the insurer pays the hospital directly, or through
            reimbursement, where the policyholder pays first and later gets
            compensated. Additionally, health insurance provides tax benefits
            under Section 80D of the Income Tax Act, making it not just a health
            necessity but also a smart financial decision.{" "}
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6 text-center">
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-800">
          Statistics of Health insurance
        </h2>
        {/* <p className="text-gray-600 text-sm md:text-base mt-2">
        Choosing the right car insurance is crucial for financial security on the road. 
        Here are the three main types of car insurance plans:
      </p> */}

        <div className="mt-6 grid md:grid-cols-3 gap-6 text-left">
          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">🌍</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">
              Coverage Expansion
            </h3>
            <p className="text-gray-700 text-sm mt-1">
              As of the fiscal year 2023, over 550 million individuals in India
              were covered under health insurance schemes, with
              government-sponsored programs accounting for the majority of these
              policies.
            </p>
          </div>

          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">💰</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">
              Rising Claim Amounts
            </h3>
            <p className="text-gray-700 text-sm mt-1">
              Over the past three years, the average health insurance claim
              amount has increased by more than 30%, reaching approximately
              ₹81,000 in the fiscal year 2025. This surge is attributed to
              escalating hospital treatment costs and policyholders opting for
              enhanced coverage.
            </p>
          </div>

          <div className="p-6 border rounded-lg shadow-md bg-white">
            <span className="text-3xl">📆</span>
            <h3 className="text-lg font-semibold text-gray-800 mt-2">
              Increased Claim Frequency
            </h3>
            <p className="text-gray-700 text-sm mt-1">
              The frequency of health insurance claims has risen from 4.9% in
              FY2023 to 6.39% in FY2025. This uptick may be due to improved
              awareness, better healthcare access, and a proactive approach to
              health management among policyholders.
            </p>
          </div>
        </div>
      </div>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <h2 className="text-2xl md:text-3xl font-bold text-center text-gray-900 mb-6">
          Types of Health Insurance
        </h2>
        <p className="text-lg text-center text-gray-600 mb-10">
          Choosing the right health insurance plan ensures financial security
          and access to quality healthcare.
        </p>

        {/* Insurance Cards */}
        <div className="grid md:grid-cols-3 sm:grid-cols-2 gap-8">
          {HealthinsuranceTypes.map((insurance, index) => (
            <div
              key={index}
              className="bg-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 text-center"
            >
              <div className="flex justify-center mb-4">{insurance.icon}</div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                {insurance.title}
              </h3>
              <p className="text-gray-600">{insurance.description}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <h2 className="text-2xl md:text-3xl font-bold text-center text-gray-900 mb-6">
          Why Do You Need Health Insurance?
        </h2>
        <p className="text-lg text-center text-gray-600 mb-10">
          Health insurance safeguards your finances and ensures access to
          quality healthcare during emergencies.
        </p>

        {/* Benefits Grid */}
        <div className="grid md:grid-cols-3 sm:grid-cols-2 gap-8">
          {benefitshealth.map((benefit, index) => (
            <div
              key={index}
              className="bg-white p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 text-center"
            >
              <div className="flex justify-center mb-4">{benefit.icon}</div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                {benefit.title}
              </h3>
              <p className="text-gray-600">{benefit.description}</p>
            </div>
          ))}
        </div>
      </div>
      {/* <div className="max-w-7xl mx-auto p-6 bg-white shadow-lg rounded-lg">
        <h2 className="text-3xl font-bold text-blue-600 mb-4">
          What is the Ideal Coverage for Health Insurance?
        </h2>
        <p className="text-gray-700 mb-4">
          Choosing the right health insurance coverage is essential to safeguard
          your finances against rising medical expenses. Experts recommend
          opting for a health cover of at least <strong>₹10 lakhs</strong> for
          adequate protection.
        </p>

        <ul className="list-disc list-inside text-gray-700 mb-6">
          <li>
            <strong>City of Residence:</strong> Higher-tier cities demand higher
            coverage due to expensive medical costs.
          </li>
          <li>
            <strong>Age and Life Stage:</strong> Older individuals or those with
            medical conditions may need higher coverage.
          </li>
          <li>
            <strong>Future Healthcare Costs:</strong> Medical inflation is
            rising, so a higher sum insured ensures long-term security.
          </li>
        </ul>

      
        <div className="overflow-x-auto">
          <table className="w-full border-collapse border border-gray-300">
            <thead className="bg-blue-600 text-white">
              <tr>
                <th className="p-3 border border-gray-300">Plan Type</th>
                <th className="p-3 border border-gray-300">Tier-1 City</th>
                <th className="p-3 border border-gray-300">Tier-2 City</th>
                <th className="p-3 border border-gray-300">Tier-3 City</th>
              </tr>
            </thead>
            <tbody>
              <tr className="bg-gray-100">
                <td className="p-3 border border-gray-300 font-semibold">
                  Individual Health Insurance
                </td>
                <td className="p-3 border border-gray-300">₹10 lakh & above</td>
                <td className="p-3 border border-gray-300">₹5-10 lakh</td>
                <td className="p-3 border border-gray-300">₹5 lakh</td>
              </tr>
              <tr>
                <td className="p-3 border border-gray-300 font-semibold">
                  Family Floater Plan
                </td>
                <td className="p-3 border border-gray-300">₹30 lakh & above</td>
                <td className="p-3 border border-gray-300">₹20 lakh & above</td>
                <td className="p-3 border border-gray-300">₹10 lakh & above</td>
              </tr>
              <tr className="bg-gray-100">
                <td className="p-3 border border-gray-300 font-semibold">
                  Senior Citizen Plan
                </td>
                <td className="p-3 border border-gray-300">₹20 lakh & above</td>
                <td className="p-3 border border-gray-300">₹15 lakh & above</td>
                <td className="p-3 border border-gray-300">₹10 lakh & above</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div> */}
        <div className="max-w-7xl mx-auto p-6 bg-white shadow-lg rounded-lg">
      <h2 className="text-2xl md:text-3xl font-bold text-blue-600 mb-4">
        What is the Ideal Coverage for Health Insurance?
      </h2>
      <p className="text-gray-700 mb-4">
        Choosing the right health insurance coverage is essential to safeguard
        your finances against rising medical expenses. Experts recommend opting
        for a health cover of at least <strong>₹10 lakhs</strong> for adequate
        protection.
      </p>

      <ul className="list-disc list-inside text-gray-700 mb-6">
        <li>
          <strong>City of Residence:</strong> Higher-tier cities demand higher
          coverage due to expensive medical costs.
        </li>
        <li>
          <strong>Age and Life Stage:</strong> Older individuals or those with
          medical conditions may need higher coverage.
        </li>
        <li>
          <strong>Future Healthcare Costs:</strong> Medical inflation is rising,
          so a higher sum insured ensures long-term security.
        </li>
      </ul>

      {/* Desktop Table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full border-collapse border border-gray-300">
          <thead className="bg-blue-600 text-white">
            <tr>
              <th className="p-3 border border-gray-300">Plan Type</th>
              <th className="p-3 border border-gray-300">Tier-1 City</th>
              <th className="p-3 border border-gray-300">Tier-2 City</th>
              <th className="p-3 border border-gray-300">Tier-3 City</th>
            </tr>
          </thead>
          <tbody>
            {data.map((row, index) => (
              <tr key={index} className={index % 2 === 0 ? "bg-gray-100" : ""}>
                <td className="p-3 border border-gray-300 font-semibold">
                  {row.plan}
                </td>
                <td className="p-3 border border-gray-300">{row.tier1}</td>
                <td className="p-3 border border-gray-300">{row.tier2}</td>
                <td className="p-3 border border-gray-300">{row.tier3}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Stacked Cards */}
      <div className="block md:hidden">
        {data.map((row, index) => (
          <div
            key={index}
            className="border border-gray-300 rounded-md mb-4 shadow-sm"
          >
            <div className="bg-blue-600 text-white font-semibold px-4 py-2 rounded-t-md">
              {row.plan}
            </div>
            <div className="px-4 py-2 border-t border-gray-300 flex justify-between">
              <span className="font-medium">Tier-1 City</span>
              <span>{row.tier1}</span>
            </div>
            <div className="px-4 py-2 border-t border-gray-300 flex justify-between">
              <span className="font-medium">Tier-2 City</span>
              <span>{row.tier2}</span>
            </div>
            <div className="px-4 py-2 border-t border-gray-300 flex justify-between">
              <span className="font-medium">Tier-3 City</span>
              <span>{row.tier3}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
      <div className=" px-2 md:px-6 bg-gray-100 mt-6">
        <div className=" p-6 min-h-screen">
          <h1 className="text-2xl md:text-3xl font-bold text-blue-700 mb-4">
            Coverage Under Health Insurance
          </h1>
          <p className="text-gray-700 mb-6">
            A comprehensive health insurance plan offers financial protection
            against a wide range of medical expenses. Below are the key
            coverages included in most health insurance policies in India:
          </p>

          {points1.map((point, index) => (
            <div
              key={index}
              className="mb-4 p-4 bg-white rounded-lg shadow-md border-l-4 border-blue-500"
            >
              <h2 className="text-xl font-semibold text-blue-600">
                {point.title}
              </h2>
              <p className="text-gray-600">{point.description}</p>
            </div>
          ))}
        </div>
      </div>
      <div className=" px-2 md:px-6 bg-gray-100">
        <div className=" p-6 min-h-screen">
          <h1 className="text-2xl md:text-3xl font-bold text-blue-700 mb-4">
            What is Not Covered in a Health Insurance Plan?
          </h1>
          <p className="text-gray-700 mb-6">
            While health insurance provides extensive coverage, certain
            exclusions exist. Here are some of the common exclusions in health
            insurance policies:
          </p>

          {points2.map((point, index) => (
            <div
              key={index}
              className="mb-4 p-4 bg-white rounded-lg shadow-md border-l-4 border-blue-500"
            >
              <h2 className="text-xl font-semibold text-blue-600">
                {point.title}
              </h2>
              <p className="text-gray-600">{point.description}</p>
            </div>
          ))}
        </div>
      </div>
      <div className=" px-2 p-6 md:px-6 bg-gray-100">
        <div className=" p-6 min-h-screen">
          <h1 className="text-2xl md:text-3xl font-bold text-blue-700 mb-4">
            Things to Consider Before Buying a Health Insurance Plan
          </h1>
          <p className="text-gray-700 mb-6">
            Choosing the right health insurance plan is crucial to ensuring
            financial security during medical emergencies. Here are some key
            factors to consider before purchasing a policy:
          </p>

          {/* Single Column Layout */}

          {points3.map((item, index) => (
            <div
              key={index}
              className="p-5 mb-4 bg-white rounded-lg shadow-md border-l-4 border-blue-500"
            >
              <h2 className="text-xl font-semibold text-blue-600">
                {item.title}
              </h2>
              <ul className="list-disc list-inside text-gray-600 mt-2">
                {item.points.map((point, idx) => (
                  <li key={idx} className="mt-1">
                    {point}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
      <div className=" px-2 p-6 md:px-6 bg-gray-100">
        <div className=" p-6 min-h-screen">
          <h1 className="text-2xl md:text-3xl font-bold text-blue-700 mb-4">
            Factors Affecting Health Insurance in India
          </h1>
          <p className="text-gray-700 mb-6">
            Several factors influence the cost, coverage, and eligibility of
            health insurance policies in India. Understanding these factors can
            help you make an informed decision while purchasing a policy.
          </p>

          {/* Single Column Layout */}

          {points4.map((item, index) => (
            <div
              key={index}
              className="p-5 mb-4 bg-white rounded-lg shadow-md border-l-4 border-blue-500"
            >
              <h2 className="text-xl font-semibold text-blue-600">
                {item.title}
              </h2>
              <ul className="list-disc list-inside text-gray-600 mt-2">
                {item.points.map((point, idx) => (
                  <li key={idx} className="mt-1">
                     {point}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
      {/* FAQ section */}
      <div className="max-w-4xl mx-auto py-6 px-2">
        <h1 className="text-2xl md:text-3xl font-semibold text-center mb-8">
          Frequently Asked Questions
        </h1>
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="border rounded-lg overflow-hidden shadow-md"
            >
              <button
                onClick={() => toggleAccordion(index)}
                className="w-full p-4 text-left text-lg font-semibold focus:outline-none flex justify-between items-center"
              >
                {faq.question}
                <Icon id={index} open={activeIndex} />
              </button>
              {activeIndex === index && (
                <div className="p-4 bg-gray-100">
                  <p className="text-gray-700">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export default HealthInsuranceNew;
